import React, { useContext, useMemo } from 'react';
import moment from 'moment';
import * as am4charts from '@amcharts/amcharts4/charts';
import { get, groupBy } from 'lodash';

import { ChartName, chartNames } from './lib/chartNames';
import { EditEvents, EditEventsAction, IEventFilterGroups, useEditEventsDispatch } from '../events/edit-events';
import { deviceEventFilterGroupNames, IDeviceEventFilterState } from '../events/deviceEventsTypes';
import { getDeviceEventFilterGroups } from '../events/deviceEvents';
import { EventsShown } from '../events-shown';
import { formatDurationData } from '../../../../../core/device/deviceFormat';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';
import { IDeviceUsage } from '../../../../../../services/core/devicePerformance';
import { DeviceEvent } from '../../../../../../services/core/eventsTypes';

import { StackedColumnsDateChart, IProps as IChartProps } from '@components/chart/stackedColumnsDateChart';
import { formatTooltipDayWithHourRange } from '@components/chart/tooltips';
import { drawXAxisLabelsFromDateRange } from '@components/chart/lib';
import { createYAxis } from '@lib/usageChartYAxis';
import { useTranslation } from '@lib/useTypedTranslation';

import './charts.css';
import '../events/deviceEvents.css';

export interface IProps {
  classname: string,
  data: IDeviceUsage[],
  dateRange: { from: number, to: number },
  platformType: string,
  events: DeviceEvent[],
  eventFilters: IDeviceEventFilterState,
  dispatchEventFilters: React.Dispatch<EditEventsAction<ChartName>>,
  visible: boolean,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart
}

export const useChartData = (props: IProps): { eventFilterGroups: IEventFilterGroups, chartProps: Pick<IChartProps<any>, "title" | "data" | "series" | "drawXAxis" | "drawYAxis"> } => {
  const { dateRange } = props;
  const { t } = useTranslation('performance');

  const featureToggles = useFeatureTogglesContext();

  const data = useMemo(() => {
    if (!props.data.length) {
      return [];
    }
    const eventsByHour = groupBy(props.events, (event) => moment.utc(event.local).startOf('hour').valueOf());
    return props.data.map(({ date, used }) => {
      return {
        date: moment.utc(parseInt(date)).valueOf(),
        tooltipDate: parseInt(date),
        used: used || 0,
        events: get(eventsByHour, date, [])
      };
    });
  }, [props.data, props.events]);

  const eventFilterGroups = getDeviceEventFilterGroups(props.platformType, featureToggles);

  const series = [
    {
      colour: '#00aa55',
      dataKey: 'used',
      description: t('IN_USE', { ns: 'performance' }),
      visible: true,
    }
  ];

  return {
    eventFilterGroups,
    chartProps: {
      title: t('DEVICE_MINUTES_USAGE_PER_HOUR', { ns: 'performance' }),
      data,
      series,
      drawXAxis: (_, xAxis) => drawXAxisLabelsFromDateRange(xAxis as am4charts.DateAxis, dateRange),
      drawYAxis: (chart, yAxis) => {
        const maxUserActivitySeconds = Math.max(...chart.data.map((data) => data['used']));
        const maxUserActivityHours = maxUserActivitySeconds / 60 / 60;
        (yAxis as am4charts.DurationAxis).baseUnit = "second";

        createYAxis(yAxis as am4charts.DurationAxis, maxUserActivityHours, t, true, true);

        yAxis.renderer.labels.template.disabled = true;
      }
    }
  };
};

export function DeviceUsage24Hours(props: IProps) {
  const { classname, eventFilters, dispatchEventFilters, onMount } = props;
  const { t } = useTranslation(['performance', 'editEvents', 'translation']);

  const {
    handleUpdate, handleApply, handleApplyAll, handleCancel
  } = useEditEventsDispatch(
    dispatchEventFilters,
    chartNames.deviceUsage24Hours,
    /* istanbul ignore next */
    () => {}
  );

  const { eventFilterGroups, chartProps } = useChartData(props);

  return (
    <div className={`core-device_performance-${classname}-device-usage`}>
      <StackedColumnsDateChart<any>
        {...chartProps}
        headerControls={<div className="chart-edit-events">
          <EventsShown eventFilters={eventFilters.appliedEventFilters} />
          <EditEvents
            subheader={`‘${chartProps.title}’`}
            eventGroupNames={deviceEventFilterGroupNames}
            eventFilterGroups={eventFilterGroups}
            eventFilterState={eventFilters.currentEventFilters}
            handleUpdate={handleUpdate}
            handleApply={handleApply}
            handleCancel={handleCancel}
            handleApplyAll={handleApplyAll}
          />
        </div>}
        cypressId="last24HoursOrSelectedDateDeviceUsage"
        hasLegend={true}
        days={1}
        yAxisType='duration'
        xAxisType={'date'}
        dateAxisEndDuration={{ amount: "1", unit: 'hour' }}
        formatTooltipDate={formatTooltipDayWithHourRange}
        useAxisTooltip
        formatData={formatDurationData(t, true)}
        eventOverlay={true}
        onMount={onMount}
        adjustChartDimensions={{ height: 250 }}
      />
    </div>
  );
}
